import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Tag, Eye, Heart, MessageCircle, Share2, Send, CheckCircle, ArrowRight, BookOpen, Bookmark, Copy, Check } from 'lucide-react';
import { useReaderAuth } from '../context/ReaderAuthContext';
import { useSavedArticles } from '../hooks/useSavedArticles';
import NewsletterCTA from '../components/NewsletterCTA';
import SEOMeta from '../components/SEOMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTHOR_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/dnctg08g_1780602847089.jpg";

/* ═══ Share Panel ═══ */
const SharePanel = ({ article }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const url = window.location.href;
  const title = article?.title || 'Third Culture Leadership';
  const excerpt = article?.excerpt || article?.meta_description || '';
  const shareText = excerpt
    ? `"${excerpt.slice(0, 160).trim()}${excerpt.length > 160 ? '…' : ''}" — Continue reading on Third Culture Leadership`
    : `${title} — Third Culture Leadership`;
  const encodedUrl   = encodeURIComponent(url);
  const encodedText  = encodeURIComponent(`${shareText}\n\n`);
  const encodedTitle = encodeURIComponent(title);

  const socials = [
    {
      id: 'linkedin', label: 'LinkedIn', color: '#0077B5', bg: 'rgba(0,119,181,0.08)',
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&title=${encodedTitle}&summary=${encodeURIComponent(excerpt.slice(0,150))}`,
    },
    {
      id: 'x', label: 'X', color: '#000', bg: 'rgba(0,0,0,0.06)',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.857L1.254 2.25H8.08l4.259 5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    },
    {
      id: 'facebook', label: 'Facebook', color: '#1877F2', bg: 'rgba(24,119,242,0.08)',
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodeURIComponent(excerpt.slice(0,100))}`,
    },
    {
      id: 'whatsapp', label: 'WhatsApp', color: '#25D366', bg: 'rgba(37,211,102,0.08)',
      icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${url}`)}`,
    },
  ];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(url); }
    catch { const ta = document.createElement('textarea'); ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative ml-auto" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="share-btn"
        className="flex items-center gap-1.5 px-4 py-2 text-xs font-sans transition-all"
        style={{ borderRadius: '20px', backgroundColor: open ? 'rgba(201,147,22,0.1)' : 'rgba(7,27,58,0.05)', color: open ? '#C99316' : 'rgba(7,27,58,0.4)' }}
      >
        <Share2 size={13} /> Share
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-50"
          style={{ width: '280px', background: '#fff', borderRadius: '14px', boxShadow: '0 8px 40px rgba(7,27,58,0.13), 0 0 0 1px rgba(7,27,58,0.07)' }}
          data-testid="share-panel"
        >
          {/* Excerpt preview */}
          <div className="px-4 pt-4 pb-3" style={{ borderBottom: '1px solid rgba(7,27,58,0.06)' }}>
            <p className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'rgba(7,27,58,0.25)' }}>Sharing this article</p>
            <p className="text-xs leading-relaxed" style={{ color: 'rgba(7,27,58,0.55)', fontStyle: 'italic' }}>
              "{excerpt ? excerpt.slice(0, 110) + (excerpt.length > 110 ? '…' : '') : title}"
            </p>
            <p className="text-[10px] mt-1.5" style={{ color: '#C99316', opacity: 0.8 }}>+ link to continue reading</p>
          </div>

          {/* 4 social icons */}
          <div className="p-3 grid grid-cols-4 gap-2">
            {socials.map(s => (
              <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer"
                 data-testid={`share-${s.id}`}
                 className="flex flex-col items-center gap-1.5 py-2.5 rounded-xl transition-all hover:scale-105"
                 style={{ background: s.bg, color: s.color }}
                 onClick={() => setOpen(false)}>
                {s.icon}
                <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.04em' }}>{s.label}</span>
              </a>
            ))}
          </div>

          {/* Copy link */}
          <div className="px-3 pb-3">
            <button onClick={handleCopy} data-testid="share-copy-link"
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-all"
              style={{ background: copied ? 'rgba(34,197,94,0.08)' : 'rgba(7,27,58,0.04)', color: copied ? '#16a34a' : 'rgba(7,27,58,0.5)' }}>
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? 'Copied!' : 'Copy article link'}
              <span className="ml-auto text-[10px] truncate max-w-[110px]" style={{ color: 'rgba(7,27,58,0.18)' }}>
                {url.replace(/^https?:\/\//, '')}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══ Article Detail ═══ */
const ArticlePage = ({ article }) => {
  const { user, isAuthenticated } = useReaderAuth();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(article?.likes_count || 0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [commentPending, setCommentPending] = useState(false);

  // Derive meta values — safe to reference before early return since article may be null
  const ogImage = article?.og_image || article?.featured_image || '';
  const ogDesc = article?.excerpt || article?.meta_description || '';
  const fullTitle = article ? `${article.title} | Third Culture Leadership` : '';

  useEffect(() => {
    if (!article) return;
    fetch(`${API_URL}/api/public/articles/${article.slug}/comments`).then(r => r.json()).then(d => setComments(d.comments || [])).catch(() => {});
    if (user?.id) {
      fetch(`${API_URL}/api/public/articles/${article.slug}/likes?reader_id=${user.id}`).then(r => r.json()).then(d => { setLiked(d.user_liked); setLikesCount(d.count); }).catch(() => {});
    }
  }, [article, user]);

  // Directly override static index.html og: tags so social sharing previews pick up article-specific values
  useEffect(() => {
    if (!article) return;
    const setMeta = (selector, attr, value) => {
      const el = document.head.querySelector(selector);
      if (el && value) el.setAttribute(attr, value);
    };
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    if (ogImage) {
      setMeta('meta[property="og:image"]', 'content', ogImage);
      setMeta('meta[property="og:image:secure_url"]', 'content', ogImage);
      setMeta('meta[name="twitter:image"]', 'content', ogImage);
    }
    if (ogDesc) {
      setMeta('meta[property="og:description"]', 'content', ogDesc);
      setMeta('meta[name="twitter:description"]', 'content', ogDesc);
    }
    document.title = fullTitle;
  }, [article, ogImage, ogDesc, fullTitle]);

  const handleLike = async () => {
    if (!isAuthenticated()) return;
    const res = await fetch(`${API_URL}/api/public/articles/${article.slug}/like?reader_id=${user.id}`, { method: 'POST' });
    if (res.ok) { const d = await res.json(); setLiked(d.liked); setLikesCount(prev => d.liked ? prev + 1 : prev - 1); }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !isAuthenticated()) return;
    setPosting(true);
    const name = user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.email?.split('@')[0];
    const res = await fetch(`${API_URL}/api/public/articles/${article.slug}/comments?reader_id=${user.id}&reader_name=${encodeURIComponent(name)}&content=${encodeURIComponent(newComment)}`, { method: 'POST' });
    if (res.ok) {
      setNewComment('');
      setCommentPending(true);
      // Don't add to the visible list — it's pending moderation
      setTimeout(() => setCommentPending(false), 5000);
    }
    setPosting(false);
  };

  if (!article) return null;

  const renderBlock = (block, i) => {
    const c = block.content || {};
    switch (block.type) {
      case 'heading': return (
        <h2 key={i} className={`font-serif ${c.level === 'h3' ? 'text-xl md:text-2xl' : c.level === 'h4' ? 'text-lg' : 'text-2xl md:text-3xl'} font-light tracking-tight mt-12 mb-5`}
            style={{ color: '#071B3A' }}>
          {c.text}
        </h2>
      );
      case 'paragraph': return (
        <p key={i} className="text-[16px] leading-[1.85] mb-5 font-sans font-light"
           style={{ color: 'rgba(7,27,58,0.68)' }}>
          {c.text}
        </p>
      );
      case 'quote': return (
        <blockquote key={i} className="my-10 relative pl-7">
          <div className="absolute left-0 top-0 bottom-0 w-[2px]" style={{ backgroundColor: '#C99316', opacity: 0.5 }} />
          <p className="font-serif text-xl md:text-2xl font-light leading-[1.5] italic mb-3"
             style={{ color: 'rgba(7,27,58,0.72)' }}>
            {c.text}
          </p>
          {c.attribution && (
            <p className="font-sans text-[10px] uppercase tracking-[0.22em]"
               style={{ color: 'rgba(201,147,22,0.75)' }}>
              — {c.attribution}
            </p>
          )}
        </blockquote>
      );
      case 'image': return (
        <figure key={i} className="my-10">
          <img src={c.url} alt={c.alt || ''} className="w-full" style={{ borderRadius: '2px' }} />
          {c.caption && <figcaption className="text-[11px] mt-3 text-center tracking-wide" style={{ color: 'rgba(7,27,58,0.35)' }}>{c.caption}</figcaption>}
        </figure>
      );
      case 'callout': return (
        <div key={i} className="my-8 p-6 md:p-7"
             style={{ backgroundColor: 'rgba(201,147,22,0.05)', borderLeft: '2px solid rgba(201,147,22,0.4)' }}>
          <p className="font-sans text-[15px] leading-[1.8] font-light" style={{ color: 'rgba(7,27,58,0.7)' }}>{c.text}</p>
        </div>
      );
      case 'list': return (
        <ul key={i} className="space-y-3 my-7">
          {(c.text || '').split('\n').filter(Boolean).map((item, j) => (
            <li key={j} className="flex items-start gap-3 font-sans text-[15px] leading-[1.75]"
                style={{ color: 'rgba(7,27,58,0.65)' }}>
              <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#C99316', opacity: 0.6 }} />
              {item}
            </li>
          ))}
        </ul>
      );
      case 'embed': return <div key={i} className="my-6 p-4 text-sm" style={{ backgroundColor: 'rgba(7,27,58,0.04)' }}>{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: '#C99316' }}>{c.url}</a> : 'Embed'}</div>;
      case 'video': return <figure key={i} className="my-8"><video src={c.url} controls className="w-full" /></figure>;
      case 'audio': return <figure key={i} className="my-8 p-4 border" style={{ borderColor: 'rgba(7,27,58,0.08)' }}><audio src={c.url} controls className="w-full" /></figure>;
      default: return null;
    }
  };

  return (
    <div style={{ backgroundColor: '#FAF8F3' }} className="min-h-screen" data-testid="article-page">
      <SEOMeta
        title={article.title}
        description={ogDesc}
        image={ogImage}
        url={`/articles/${article.slug}`}
        type="article"
        imageAlt={article.title}
        imageWidth="1536"
        imageHeight="1024"
      />
      {/* ── Article hero header ────────────────────────────────────────────── */}
      <div style={{ backgroundColor: '#071B3A' }} className="pt-24 pb-14 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <Link to="/articles" className="inline-flex items-center gap-1.5 mb-8 transition-colors duration-200"
                style={{ color: 'rgba(250,248,243,0.3)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'sans-serif' }}>
            <ArrowLeft size={11} /> All Articles
          </Link>
          {article.categories?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {article.categories.map(c => (
                <span key={c} className="font-sans text-[9px] uppercase tracking-[0.22em] px-2.5 py-1"
                      style={{ backgroundColor: 'rgba(201,147,22,0.12)', color: '#C99316' }}>
                  {c}
                </span>
              ))}
            </div>
          )}
          <h1 className="font-serif font-light tracking-tight leading-[1.1] mb-5"
              style={{ fontSize: 'clamp(1.9rem, 4vw, 3rem)', color: '#FAF8F3' }}>
            {article.title}
          </h1>
          {article.excerpt && (
            <p className="font-sans font-light leading-[1.75] mb-7"
               style={{ fontSize: '1rem', color: 'rgba(250,248,243,0.5)', maxWidth: '560px' }}>
              {article.excerpt}
            </p>
          )}
          <div className="flex items-center gap-3" style={{ color: 'rgba(250,248,243,0.35)', fontSize: '12px' }}>
            <img src={AUTHOR_IMG} alt={article.author} className="w-8 h-8 rounded-full object-cover object-top"
                 style={{ border: '1px solid rgba(201,147,22,0.25)' }} />
            <span className="font-sans">{article.author}</span>
            <span style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: 'rgba(250,248,243,0.2)', display: 'inline-block' }} />
            <span className="font-sans flex items-center gap-1"><Clock size={10} /> {article.reading_time || 3} min read</span>
          </div>
        </div>
      </div>

      {/* ── Thumbnail ────────────────────────────────────────────────────── */}
      {article.featured_image && (
        <div className="max-w-3xl mx-auto px-5 sm:px-8 -mt-1">
          <img src={article.featured_image} alt={article.title}
               className="w-full object-cover"
               style={{ maxHeight: '400px', display: 'block' }} />
        </div>
      )}

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-14 pb-10">
        <div className="article-content" style={{ maxWidth: '680px' }}>
          {(article.content_blocks || []).map(renderBlock)}
        </div>

        {/* ── Author block ─────────────────────────────────────────────── */}
        <div className="mt-14 pt-8 mb-8" style={{ borderTop: '1px solid rgba(7,27,58,0.08)' }}>
          <div className="flex items-start gap-4">
            <img src={AUTHOR_IMG} alt={article.author} className="w-12 h-12 rounded-full object-cover object-top flex-shrink-0"
                 style={{ border: '1px solid rgba(201,147,22,0.25)' }} />
            <div>
              <p className="font-serif text-base font-light" style={{ color: '#071B3A' }}>{article.author}</p>
              <p className="font-sans text-[11px] mt-0.5" style={{ color: 'rgba(7,27,58,0.4)' }}>
                Paediatrician · Author · Founder, Third Culture Leadership
              </p>
            </div>
          </div>
        </div>

        {/* ── Engagement bar ───────────────────────────────────────────── */}
        <div className="flex items-center gap-4 pt-5" style={{ borderTop: '1px solid rgba(7,27,58,0.07)' }} data-testid="article-engagement">
          <button onClick={handleLike} disabled={!isAuthenticated()} className="flex items-center gap-1.5 px-4 py-2 text-xs font-sans transition-all"
                  style={{ borderRadius: '20px', backgroundColor: liked ? 'rgba(239,68,68,0.08)' : 'rgba(7,27,58,0.05)', color: liked ? '#EF4444' : 'rgba(7,27,58,0.35)', opacity: !isAuthenticated() ? 0.4 : 1 }}
                  data-testid="like-btn">
            <Heart size={14} fill={liked ? 'currentColor' : 'none'} /> {likesCount}
          </button>
          <span className="flex items-center gap-1 text-xs" style={{ color: 'rgba(7,27,58,0.25)' }}><MessageCircle size={14} /> {comments.length}</span>
          <SharePanel article={article} />
        </div>

        {article.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-5">
            {article.tags.map(t => (
              <span key={t} className="font-sans text-[10px] px-2 py-0.5 flex items-center gap-1"
                    style={{ backgroundColor: 'rgba(7,27,58,0.05)', color: 'rgba(7,27,58,0.3)' }}>
                <Tag size={8} />{t}
              </span>
            ))}
          </div>
        )}

        {/* Comments */}
        <section className="mt-12" data-testid="comments-section">
          <h3 className="text-navy font-serif text-lg font-semibold mb-5">Discussion ({comments.length})</h3>

          {isAuthenticated() ? (
            <form onSubmit={handleComment} className="mb-8">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={3}
                placeholder="Share your thoughts..."
                className="w-full bg-white border border-navy/[0.1] rounded-xl px-4 py-3 text-sm text-navy placeholder-navy/30 resize-none focus:outline-none focus:border-gold/40 focus:ring-1 focus:ring-gold/20 mb-2"
                data-testid="comment-input"
              />
              <button type="submit" disabled={posting || !newComment.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 bg-gold text-navy text-xs font-semibold rounded-lg hover:bg-gold-light disabled:opacity-40 transition-all" data-testid="comment-submit">
                <Send size={12} /> Post Comment
              </button>
              {commentPending && (
                <p className="mt-2 text-xs text-navy/40 flex items-center gap-1.5" data-testid="comment-pending-msg">
                  <CheckCircle size={11} className="text-green-500" />
                  Your comment is under review and will appear once approved.
                </p>
              )}
            </form>
          ) : (
            <div className="mb-8 p-5 bg-white border border-navy/[0.08] rounded-xl text-center">
              <p className="text-navy/40 text-sm mb-2">Sign in to like and comment.</p>
              <Link to="/signin" className="text-gold text-xs hover:text-gold-light">Sign In <ArrowRight size={10} className="inline" /></Link>
            </div>
          )}

          <div className="space-y-3">
            {comments.map((c) => (
              <div key={c.id} className="flex gap-3 bg-white border border-navy/[0.06] rounded-xl p-4 shadow-sm shadow-navy/[0.02]">
                <div className="w-8 h-8 bg-gold/[0.1] rounded-full flex items-center justify-center flex-shrink-0 text-gold/80 text-xs font-bold">{(c.reader_name || 'R')[0].toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-navy/70 text-xs font-medium">{c.reader_name}</span>
                    <CheckCircle size={10} className="text-blue-400" />
                    <span className="text-navy/20 text-[10px]">{new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                  </div>
                  <p className="text-navy/55 text-base leading-relaxed">{c.content}</p>
                </div>
              </div>
            ))}
            {comments.length === 0 && <p className="text-navy/25 text-xs text-center py-6">No comments yet. Be the first to share your perspective.</p>}
          </div>
        </section>

        {/* Related CTA */}
        <div className="mt-14 pt-8 border-t border-navy/[0.08] text-center">
          <p className="text-navy/25 text-xs mb-3">Explore more insights</p>
          <Link to="/articles" className="inline-flex items-center gap-2 px-5 py-2.5 border border-navy/15 text-navy/50 text-base rounded-full hover:border-gold/40 hover:text-gold transition-all">
            All Articles <ArrowRight size={14} />
          </Link>
        </div>

        {/* Newsletter CTA */}
        <NewsletterCTA variant="light" source="article" className="mt-10" />
      </article>
    </div>
  );
};

/* ═══ Articles List ═══ */

const ArticleCard = ({ a, isReaderLoggedIn, savedIds, toggleSave }) => {
  const isSaved = savedIds.has(a.id);
  return (
    <div key={a.id || a.slug} className="relative group/card">
      {isReaderLoggedIn && (
        <button
          onClick={() => toggleSave({ id: a.id, slug: a.slug, title: a.title, excerpt: a.excerpt || '', image: a.featured_image || '', category: a.categories?.[0] || '' })}
          className="absolute top-2.5 right-2.5 z-10 p-1.5 rounded-full transition-all duration-200"
          style={{ backgroundColor: isSaved ? 'rgba(201,147,22,0.25)' : 'rgba(7,27,58,0.08)' }}
          title={isSaved ? 'Remove from saved' : 'Save article'}
          data-testid={`save-article-btn-${a.id}`}
        >
          <Bookmark size={13} fill={isSaved ? '#C99316' : 'none'} stroke={isSaved ? '#C99316' : '#071B3A'} />
        </button>
      )}
      <Link to={`/articles/${a.slug}`} className="block bg-white border border-navy/[0.07] rounded-xl overflow-hidden hover:border-gold/25 hover:shadow-md hover:shadow-navy/[0.06] transition-all group shadow-sm shadow-navy/[0.03]" data-testid={`article-card-${a.slug}`}>
        {a.featured_image && <img src={a.featured_image} alt={a.title} className="w-full h-40 object-cover" />}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-2">
            {a.categories?.[0] && <span className="text-[9px] uppercase tracking-wider text-gold/70">{a.categories[0]}</span>}
            <span className="text-navy/20 text-[10px] flex items-center gap-1"><Clock size={8} />{a.reading_time || 3} min</span>
          </div>
          <h3 className="font-serif text-lg text-navy font-semibold group-hover:text-gold transition-colors mb-2 leading-tight">{a.title}</h3>
          {a.excerpt && <p className="text-navy/40 text-sm line-clamp-2 leading-relaxed">{a.excerpt}</p>}
          <div className="flex items-center gap-3 mt-3 text-navy/20 text-xs">
            <span className="flex items-center gap-1"><img src={AUTHOR_IMG} alt="" className="w-4 h-4 rounded-full object-cover object-top" />{a.author}</span>
            {a.likes_count > 0 && <span className="flex items-center gap-1"><Heart size={10} />{a.likes_count}</span>}
            {a.comments_count > 0 && <span className="flex items-center gap-1"><MessageCircle size={10} />{a.comments_count}</span>}
            {a.views_count > 0 && <span className="flex items-center gap-1"><Eye size={10} />{a.views_count}</span>}
          </div>
        </div>
      </Link>
    </div>
  );
};

const ArticlesList = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');

  const { isAuthenticated } = useReaderAuth();
  const isReaderLoggedIn = typeof isAuthenticated === 'function' ? isAuthenticated() : false;
  const { savedIds, toggleSave } = useSavedArticles();

  useEffect(() => {
    const params = new URLSearchParams();
    if (activeCategory) params.set('category', activeCategory);
    fetch(`${API_URL}/api/public/articles?limit=50&${params}`).then(r => r.json()).then(d => { setArticles(d.articles || []); setLoading(false); }).catch(() => setLoading(false));
  }, [activeCategory]);

  const featured = articles.find(a => a.pinned || a.featured);
  const rest = articles.filter(a => a !== featured);
  const categories = [...new Set(articles.flatMap(a => a.categories || []))];

  return (
    <div className="bg-cream min-h-screen" data-testid="articles-page">
      {/* Hero */}
      <section className="relative pt-28 pb-14">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-gold/[0.04] rounded-full blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <p className="text-[11px] uppercase tracking-[0.3em] text-gold/70 font-sans font-medium mb-4">Insights</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-navy leading-[1.1] font-bold mb-4">Articles</h1>
          <p className="text-navy/45 text-base max-w-lg">Perspectives on cultural fluency, leadership, and navigating difference with clarity.</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
      </section>

      {/* Featured Article */}
      {featured && !loading && (
        <section className="bg-cream py-10">
          <div className="max-w-5xl mx-auto px-5 sm:px-8">
            <Link to={`/articles/${featured.slug}`} className="block bg-white border border-gold/[0.15] rounded-2xl p-6 sm:p-8 hover:border-gold/30 hover:shadow-md hover:shadow-navy/[0.06] transition-all group shadow-sm shadow-navy/[0.04]" data-testid="featured-article">
              <div className="flex flex-col md:flex-row gap-6">
                {featured.featured_image && <img src={featured.featured_image} alt={featured.title} className="w-full md:w-48 h-32 md:h-auto rounded-xl object-cover flex-shrink-0" />}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[9px] uppercase tracking-wider bg-gold/10 text-gold/80 px-2 py-0.5 rounded font-semibold">Featured</span>
                    {featured.categories?.[0] && <span className="text-[9px] uppercase tracking-wider text-navy/30">{featured.categories[0]}</span>}
                  </div>
                  <h2 className="font-serif text-2xl sm:text-3xl text-navy font-bold group-hover:text-gold transition-colors mb-3 leading-tight">{featured.title}</h2>
                  {featured.excerpt && <p className="text-navy/40 text-sm leading-relaxed mb-4 max-w-lg">{featured.excerpt}</p>}
                  <div className="flex items-center gap-3 text-navy/25 text-xs">
                    <img src={AUTHOR_IMG} alt="" className="w-5 h-5 rounded-full object-cover object-top" />
                    <span>{featured.author}</span>
                    <span className="w-1 h-1 rounded-full bg-navy/15" />
                    <span>{featured.reading_time || 3} min read</span>
                    {featured.views_count > 0 && <span className="flex items-center gap-1"><Eye size={10} />{featured.views_count}</span>}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Category Filters */}
      {categories.length > 0 && (
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            <button onClick={() => setActiveCategory('')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${!activeCategory ? 'bg-gold/10 text-gold border border-gold/25' : 'bg-navy/[0.04] text-navy/35 border border-transparent hover:text-navy/60'}`}>
              All
            </button>
            {categories.map((cat) => (
              <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${activeCategory === cat ? 'bg-gold/10 text-gold border border-gold/25' : 'bg-navy/[0.04] text-navy/35 border border-transparent hover:text-navy/60'}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Articles Grid / Category Sections */}
      <section className="bg-[#EDE9E0] py-12 pb-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          {loading ? <div className="py-16 text-center text-navy/25">Loading...</div> : rest.length === 0 && !featured ? (
            <div className="py-16 text-center">
              <BookOpen size={32} className="mx-auto text-navy/15 mb-3" />
              <p className="text-navy/35 text-sm">No published articles yet. Check back soon.</p>
            </div>
          ) : (
            /* Flat grid — all articles, filtered by category if one is active */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rest.map((a) => (
                <ArticleCard key={a.id || a.slug} a={a} isReaderLoggedIn={isReaderLoggedIn} savedIds={savedIds} toggleSave={toggleSave} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Leadership insight band */}
      <section className="bg-cream py-12 border-y border-navy/[0.06]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold/60 font-sans mb-4">From the Author</p>
          <blockquote className="font-serif text-xl sm:text-2xl text-navy/70 font-light leading-[1.4] italic mb-5">
            "Cultural fluency without compromise means knowing who you are well enough that adaptation never becomes loss."
          </blockquote>
          <cite className="not-italic text-[11px] uppercase tracking-[0.2em] text-navy/35 font-sans">— Promise Monday</cite>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="bg-cream pb-20 pt-10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <NewsletterCTA variant="light" source="article" />
        </div>
      </section>
    </div>
  );
};

/* ═══ Router ═══ */
const PublicArticles = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(!!slug);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API_URL}/api/public/articles/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { setArticle(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (slug) {
    if (loading) return <div className="bg-cream min-h-screen pt-32 text-center text-navy/25">Loading...</div>;
    if (!article) return <div className="bg-cream min-h-screen pt-32 text-center text-navy/35">Article not found.</div>;
    return <ArticlePage article={article} />;
  }
  return <ArticlesList />;
};

export default PublicArticles;
